import * as Sentry from "@sentry/react";
import { createRoot, hydrateRoot } from "react-dom/client";
import App from "./App";
import { readWindowSeoInitial } from "./lib/seo-context";

// ─── Error monitoring ─────────────────────────────────────────────────────────
// Only initialise when a DSN is provided (i.e. in production builds).
// The SDK automatically captures unhandled errors and unhandled promise
// rejections, and decoded stack traces are available in Sentry thanks to the
// source maps uploaded by @sentry/vite-plugin during the build.
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.2,
  });
}
// ─────────────────────────────────────────────────────────────────────────────
import {
  LOCALE_PREF_KEY,
  getBrowserLocale,
  parseLocaleFromPath,
  withLocale,
  DEFAULT_LOCALE,
} from "./lib/locale";
import "./index.css";

// ─── Auto-redirect to browser language on first visit ────────────────────────
// Runs synchronously before React mounts to avoid any visible flash.
// Algorithm:
//   1. If a locale preference is already saved, honour it — never redirect.
//   2. If the user is already on a non-English path (e.g. a direct link to
//      /fr/properties/some-riad), treat that as their explicit choice, persist
//      it, and skip the redirect.
//   3. Otherwise (English root, no saved pref) detect the browser language.
//      If it maps to a supported non-English locale, redirect to the equivalent
//      path and persist the choice. English (or unsupported) browsers stay put.
//
// We use a flag rather than throwing to abort React mounting — throwing during
// script execution can cause some browsers to cancel the pending navigation
// that was just issued via window.location.replace().
let _localeRedirecting = false;
(function applyLocaleRedirect() {
  // Guard: localStorage may be unavailable in private browsing / sandboxed
  // iframes. If reading it throws, bail out and let the app render normally.
  let saved: string | null;
  try {
    saved = localStorage.getItem(LOCALE_PREF_KEY);
  } catch {
    return;
  }
  if (saved) return; // preference already on record — nothing to do

  // Strip the Vite base-path prefix (e.g. "/riadata") so parseLocaleFromPath
  // always receives an app-relative path like "/properties" instead of
  // "/riadata/properties". We re-apply the prefix when building the target.
  const baseUrl = (import.meta.env.BASE_URL ?? "/").replace(/\/$/, "");
  const rawPath = window.location.pathname;
  const appPath =
    baseUrl && rawPath.startsWith(baseUrl)
      ? rawPath.slice(baseUrl.length) || "/"
      : rawPath;

  const { locale: currentLocale, rest } = parseLocaleFromPath(appPath);

  if (currentLocale !== DEFAULT_LOCALE) {
    // Already on a locale-prefixed path → record as deliberate choice.
    try { localStorage.setItem(LOCALE_PREF_KEY, currentLocale); } catch { /* ignore */ }
    return;
  }

  const browserLocale = getBrowserLocale();
  if (!browserLocale) {
    // English or unsupported browser language → stay on English.
    try { localStorage.setItem(LOCALE_PREF_KEY, DEFAULT_LOCALE); } catch { /* ignore */ }
    return;
  }

  // Redirect to the same path under the detected locale.
  try { localStorage.setItem(LOCALE_PREF_KEY, browserLocale); } catch { /* ignore */ }
  const target = baseUrl + withLocale(browserLocale, rest);
  window.location.replace(target);
  // Signal that we are navigating away — React must not mount on this page.
  _localeRedirecting = true;
})();
// ─────────────────────────────────────────────────────────────────────────────

if (!_localeRedirecting) {
  const container = document.getElementById("root")!;
  const seoInitial = readWindowSeoInitial();
  const isPrerendered = container.hasAttribute("data-prerendered");

  if (isPrerendered) {
    hydrateRoot(container, <App seoInitial={seoInitial} />);
  } else {
    createRoot(container).render(<App seoInitial={seoInitial} />);
  }
}
